import React from 'react'
import {useToggle} from '../lib'
import {Avatar, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle} from '@mui/material'
import {IconNoAvatar, IconUpload} from '../Icons'
import ReactCrop from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'

// The flow is:
//
// 1. preview image clicked which triggers native file select dialog
// 2. uploadedImg is then provided to ReactCrop component
// 3. ReactCrop updates croppedRegion as user drags & drops
// 4. once user saves, croppedRegion & imgRef are used to create a new image blob
// 5. new image blob is passed to caller via onUploaded(blob) callback

// interface ImageUploadProps {
//   blob: Blob
// }

interface ImageUploaderProps {
  value: string | null
  onChange: (blob: Blob) => void
  width: number
  height: number
  invalid?: boolean
}

export default function ImageUploader({
  value,
  onChange,
  width = 100,
  height = 100,
  invalid = false,
}: ImageUploaderProps) {
  const [uploadedImg, setUploadedImg] = React.useState('')
  const [imgRef, setImgRef] = React.useState<HTMLImageElement>()
  const previewRef = React.useRef<HTMLCanvasElement>(null)
  const [croppedRegion, setCroppedRegion] = React.useState({
    unit: '%',
    x: 0,
    y: 0,
    width: 100,
    height: 100,
  } as ReactCrop.Crop)
  const [cropped, setCropped] = React.useState(false)
  const modal = useToggle()
  const [mimeType, setMimeType] = React.useState('')

  const onFileSelected = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const tgt = evt.target
    if (!tgt?.files) {
      return
    }
    if (tgt.files.length > 0) {
      setMimeType(tgt.files[0].type)
      const reader = new FileReader()
      reader.addEventListener('load', () => setUploadedImg(String(reader.result)))
      reader.readAsDataURL(tgt.files[0])
      modal.on()
      evt.target.value = ''
    }
  }

  const onLoad = React.useCallback(
    (img: HTMLImageElement) => {
      setImgRef(img)
      // set crop region that fits given ratio
      const ratio = width / height
      const region = {
        unit: 'px',
        x: 0,
        y: 0,
        width: img.width,
        height: img.height,
      } as ReactCrop.Crop
      if (ratio > 1) {
        if (ratio < img.width / img.height) {
          region.width = img.height * ratio
          region.x = img.width / 2 - region.width / 2
        } else {
          region.height = img.width / ratio
          region.y = img.height / 2 - region.height / 2
        }
      } else if (ratio > img.width / img.height) {
        region.height = img.width / ratio
        region.y = img.height / 2 - region.height / 2
      } else {
        region.width = img.height * ratio
        region.x = img.width / 2 - region.width / 2
      }
      setCroppedRegion(region)
      return false
    },
    [width, height]
  )

  function onCancel() {
    modal.off()
  }

  function onSave() {
    if (!croppedRegion.width || !croppedRegion.height) {
      return
    }
    if (croppedRegion.x == null || croppedRegion.y == null) {
      return
    }
    const image = imgRef
    const canvas = previewRef.current
    if (!image || !canvas) {
      return
    }

    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      console.error('failed to load image')
      return
    }
    const pixelRatio = window.devicePixelRatio

    canvas.width = croppedRegion.width * pixelRatio
    canvas.height = croppedRegion.height * pixelRatio

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
    ctx.imageSmoothingQuality = 'high'

    ctx.drawImage(
      image,
      croppedRegion.x * scaleX,
      croppedRegion.y * scaleY,
      croppedRegion.width * scaleX,
      croppedRegion.height * scaleY,
      0,
      0,
      croppedRegion.width,
      croppedRegion.height
    )
    canvas.toBlob(blob => blob && onChange(blob), mimeType)
    setCropped(true)
    modal.off()
  }

  function CurrentImage() {
    if (cropped) {
      return null
    }
    if (value) {
      return (
        <Avatar
          src={value}
          alt="current"
          sx={{width, height}}
        />
      )
    }
    return <IconNoAvatar sx={{fontSize: height}} color="action"/>
  }

  return (
    <>
      <Box display="flex" alignItems="center" justifyContent="space-evenly">
        <Button component="label" variant="outlined">
          <IconUpload /> &nbsp; Upload
          <input type="file" onChange={onFileSelected} hidden />
        </Button>
        <CurrentImage />
        <canvas
          ref={previewRef}
          style={{
            maxWidth: Math.min(width, croppedRegion.width || 0),
            maxHeight: Math.min(height, croppedRegion.height || 0),
            display: cropped ? 'inline' : 'none',
          }}
        />
      </Box>
      <Dialog open={modal.enabled} onClose={onCancel} aria-labelledby="dialog-title">
        <DialogTitle id="dialog-title">Adjust Image</DialogTitle>
        <DialogContent>
          <ReactCrop src={uploadedImg} onImageLoaded={onLoad} crop={croppedRegion} onChange={setCroppedRegion} />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} autoFocus>
            Cancel
          </Button>
          <Button onClick={onSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
